import { Injector } from "@angular/core";
import { DatePipe, CurrencyPipe } from "@angular/common";
import * as jp from 'jsonpath';
import { ValueGetterParams } from "ag-grid-community";
import { LunaDomain } from "../../../../builder/models/domain.model";
import { AgentType, AgentTypeOptions } from "../../../../admin/models/contact.model";

export class ValueFormatterFactory {

    static createFormatter(injector: Injector, type?: ValueFormatter, options?: FormatterOptions<any>) {
        let formatter: ValueFormatter = (type != null) ? type : ValueFormatter.None;
        return ValueFormatterFactory.getFunc(formatter).bind(null, injector, options);
    }

    public static getFunc(type: ValueFormatter): (injector: Injector, options?: FormatterOptions<any>, params?: ValueGetterParams) => string {
        switch (type) {
            case ValueFormatter.Date:
                return ValueFormatterFactory.dateFormatter;
            case ValueFormatter.ArraySelector:
                return ValueFormatterFactory.arraySelectorFormatter;
            case ValueFormatter.Currency:
                return ValueFormatterFactory.currencyFormatter;
            case ValueFormatter.FlagEnum:
                return ValueFormatterFactory.flagEnumFormatter;
            case ValueFormatter.Domain:
                return ValueFormatterFactory.domainFormatter;
            case ValueFormatter.AgentTypeFlagEnumText:
                return ValueFormatterFactory.agentTypeFlagEnumTextFormatter;
            default:
                return (injector: Injector, options?: FormatterOptions, params?: ValueGetterParams) => {
                    if (params != null) {
                        var path = "$." + params.colDef.field;
                        return ValueFormatterFactory.tryGetValue(params.data, path);
                    }
                    return "";
                }
        }
    }

    public static dateFormatter(injector: Injector, options?: FormatterOptions, params?: ValueGetterParams): string {
        var datePipe = injector.get(DatePipe);
        let value: string = "";
        if (params != null && datePipe != null) {
            var path = "$." + params.colDef.field;
            value = ValueFormatterFactory.tryGetValue(params.data, path);
            let transformed = datePipe?.transform(value, 'MM/dd/yyyy')
            if (transformed != null) {
                value = transformed;
            }
        }        
        return value;
    }

    public static arraySelectorFormatter(injector: Injector, options?: FormatterOptions<any>, params?: ValueGetterParams): string {
        let result: string = "";
        if (options != null && params != null) {
            try
            {
               var path = "$." + params.colDef.field;
                result = options.params(ValueFormatterFactory.tryGetValue(params.data, path));
               if (result == null) {
                    result = "";
               }
            }
            catch (e)
            {
                console.error(e);
                result = "";
            }
        }
       
        return result;
    }


    public static currencyFormatter(injector: Injector, options?: FormatterOptions<currencyOptions>, params?: ValueGetterParams): string {
        let value: string = "";
        if (!options || !options.params) {
            options = {
                params: {} as any
            };
        }
        var currencyPipe = injector.get(CurrencyPipe);
        if (params != null) {
            let value = params.data;
            var path = "$." + params.colDef.field;
            if (currencyPipe != null) {
                value = currencyPipe?.transform(
                    ValueFormatterFactory.tryGetValue(params.data, path),
                    options.params.code,
                    options.params.display,
                    options.params.digits,
                    options.params.locale);
            }
        }
        return value;
    }

    public static flagEnumFormatter(injector: Injector, options?: FormatterOptions<flagEnumOptions>, params?: ValueGetterParams): string {
        if (!options || !options.params) {
            options = {
                params: {} as any
            };
        }
        var yesNoResult = "No";
        if (params != null) {
            var path = "$." + params.colDef.field;
            var resolvedValue = ValueFormatterFactory.tryGetValue(params.data, path);       
            if (options.params && options.params.enumObject ) {
                yesNoResult = (resolvedValue & options.params.enumObject[options.params.selectedFlagEnumValue]) > 0 ? "Yes" : "No";
            }           
        }        
        return yesNoResult;
    }


	public static domainFormatter(injector: Injector, options?: FormatterOptions<DomainOptions>, params?: ValueGetterParams): string {
		let resolvedFieldValue: any = "";
        if (!options || !options.params) {
            options = {
				params: {} as any
			};
        }
		if (params != null) {
            let path = "$." + params.colDef.field;
		    resolvedFieldValue = ValueFormatterFactory.tryGetValue(params.data, path);
		    if (options.params && options.params.domainValues) {
			    let domainIdPropertyPath = "$." + options.params.idProperty;
			    let domainDisplayPropertyPath = "$." + options.params.displayProperty;
			    //find relevant domain value
			    if (options.params.domainValues.values != null) {
				    let relevantDomainValue = options.params.domainValues.values.filter(x => ValueFormatterFactory.tryGetValue(x, domainIdPropertyPath) == resolvedFieldValue);
				    if (relevantDomainValue && relevantDomainValue.length > 0) {
					    resolvedFieldValue = ValueFormatterFactory.tryGetValue(relevantDomainValue[0], domainDisplayPropertyPath);
				    }
			    }
		    }
        }	
		return resolvedFieldValue;
        
    }

    public static agentTypeFlagEnumTextFormatter(injector: Injector, options?: FormatterOptions<flagEnumOptions>, params?: ValueGetterParams): string {
        if (!options || !options.params) {
             options = {
                params: {} as any
            };
        }
        let result: string = params?.data ?? "";  
        if (params != null) {
            var path = "$." + params.colDef.field;
            var resolvedValue = ValueFormatterFactory.tryGetValue(params.data, path);

            let contactTypes: AgentType = resolvedValue;
            let types: AgentType[] = [];
            let displayStrings: string[] = [];

            for (let prop in AgentType) {
                if (typeof AgentType[prop] === 'number' && contactTypes & (<any>AgentType[prop])) {
                    types.push(<any>AgentType[prop]);
                }                
            }
            

            types.forEach((x) => {
                let description: string = '';
                switch (x) {
                    case AgentType.AuthorizedAgent:
                        description += AgentTypeOptions.AuthorizedAgent.text;
                    break;
                    case AgentType.DesignatedRep:
                        description += AgentTypeOptions.DesignatedRepresentative.text;
                    break;
                    case AgentType.EmergencyPOC:
                        description += AgentTypeOptions.EmergencyPOC.text;
                    break;
                    case AgentType.ExecutiveAuthority:
                        description += AgentTypeOptions.ExecutiveAuthority.text;
                    break;
                    case AgentType.PrimaryContact:
                        description += AgentTypeOptions.PrimaryContact.text;
                    break;
                    case AgentType.StatutoryAgent:
                        description += AgentTypeOptions.StatutoryAgent.text;
                    break;
                    default:
                        description += AgentType[x];
                    break;
                }
                displayStrings.push(description);
            });

        //If we have no entries add in the empty flag value
            if (displayStrings.length == 0) {
                displayStrings.push(AgentTypeOptions.None.text);
            }           
            result = displayStrings.join(', ');
        }
        return result;
    }
    

    public static tryGetValue(data: any, path: string): any {
        let value = "";
        try {
            value = jp.value(data, path);
        } catch (error) {
            console.error(error);
        }
        return value;
    }
}


export type currencyOptions = {
    code: string,
    display: 'code' | 'symbol' | 'symbol-narrow' | boolean,
    digits: string,
    locale: string
}

export enum ValueFormatter {
    None = "None",
    Date = "Date",
    ArraySelector = "Array Selector",
    Currency = "Currency",
    FlagEnum = "FlagEnum",
    Domain = "Domain",
    AgentTypeFlagEnumText = "AgentTypeFlagEnumText",
}

export interface FormatterOptions<T = {}> {
    params: T;
}

export type flagEnumOptions = {
    selectedFlagEnum: KnownFlagEnums,
    selectedFlagEnumValue: string,
    enumObject: any,
}

export type DomainOptions = {
    idProperty: string,
    displayProperty: string,
    domain: any,
    domainValues?: LunaDomain
}

export enum KnownFlagEnums {
    AgentType = "AgentType",
    WellPermittingModifiers = "WellPermittingModifiers",
}